import {
  CompanyActionPermission,
  CompanyPermission,
} from '../models/company-permission.model';
import {
  ACCOUNTS_PAYABLE_PERMISSION_MODULE,
  AccountsPayableAction,
  AccountsPayableActionName,
  AccountsPayableBackendAction,
} from '../models/accounts-payable-actions.model';
import { RoleConstant } from '../models/role-constant';
import {
  getCompanyPermissionForCompany,
  getPrimaryApPagePermission,
  usesApiPagePermissions,
} from './company-permission.util';

type ApActionMap = Map<string, boolean>;

function buildApActionMap(
  actionPermissions: CompanyActionPermission[]
): ApActionMap {
  const map: ApActionMap = new Map();

  for (const permission of actionPermissions) {
    if (permission.module === ACCOUNTS_PAYABLE_PERMISSION_MODULE) {
      map.set(permission.action, permission.isAllowed === true);
    }
  }

  return map;
}

function hasApAction(map: ApActionMap, action: string): boolean {
  return map.has(action);
}

function isApActionAllowed(map: ApActionMap, action: string): boolean {
  return map.get(action) === true;
}

function hasManagerLevelEditActions(map: ApActionMap): boolean {
  return (
    hasApAction(map, AccountsPayableBackendAction.EditApprovedOrPaymentPending) ||
    hasApAction(map, AccountsPayableBackendAction.BillEdit)
  );
}

function isManagerLevelEditAllowed(map: ApActionMap): boolean {
  return (
    isApActionAllowed(
      map,
      AccountsPayableBackendAction.EditApprovedOrPaymentPending
    ) || isApActionAllowed(map, AccountsPayableBackendAction.BillEdit)
  );
}

/**
 * Resolves a frontend action from BE actionPermissions, including BE alias names.
 * Returns undefined when no matching BE action is present (caller may use legacy).
 */
export function resolveFeActionFromBeActionMap(
  map: ApActionMap,
  feAction: AccountsPayableActionName | string,
  companyPermission?: CompanyPermission | null
): boolean | undefined {
  switch (feAction) {
    case AccountsPayableAction.Create: {
      if (
        isApActionAllowed(map, AccountsPayableBackendAction.Create) ||
        isApActionAllowed(map, AccountsPayableBackendAction.CreateDraft)
      ) {
        return true;
      }
      if (
        hasApAction(map, AccountsPayableBackendAction.Create) ||
        hasApAction(map, AccountsPayableBackendAction.CreateDraft)
      ) {
        return false;
      }
      return undefined;
    }

    case AccountsPayableAction.View: {
      if (isApActionAllowed(map, AccountsPayableBackendAction.View)) {
        return true;
      }

      if (hasApAction(map, AccountsPayableBackendAction.View)) {
        return false;
      }

      // BE often omits View when other AP actions or pagePermissions are granted.
      if (map.size > 0) {
        return true;
      }

      // Any role: AP pagePermissions alone grant view/list access.
      if (getPrimaryApPagePermission(companyPermission)?.subMenus?.length) {
        return true;
      }

      return undefined;
    }

    case AccountsPayableAction.Update: {
      if (isApActionAllowed(map, AccountsPayableBackendAction.Update)) {
        return true;
      }

      if (hasApAction(map, AccountsPayableBackendAction.Update)) {
        return false;
      }

      return undefined;
    }

    case AccountsPayableAction.EditDraftOrReturned: {
      if (
        isApActionAllowed(map, AccountsPayableBackendAction.EditDraftOrReturned) ||
        isApActionAllowed(map, AccountsPayableBackendAction.UpdateDraft) ||
        isApActionAllowed(map, AccountsPayableBackendAction.EditReturned)
      ) {
        return true;
      }

      if (
        isApActionAllowed(map, AccountsPayableBackendAction.Update) &&
        !isManagerLevelEditAllowed(map)
      ) {
        return true;
      }

      if (
        hasApAction(map, AccountsPayableBackendAction.EditDraftOrReturned) ||
        hasApAction(map, AccountsPayableBackendAction.UpdateDraft) ||
        hasApAction(map, AccountsPayableBackendAction.EditReturned)
      ) {
        return false;
      }

      if (
        hasApAction(map, AccountsPayableBackendAction.Update) &&
        hasManagerLevelEditActions(map)
      ) {
        return false;
      }

      if (hasApAction(map, AccountsPayableBackendAction.Update)) {
        return isApActionAllowed(map, AccountsPayableBackendAction.Update);
      }

      return undefined;
    }

    case AccountsPayableAction.EditApprovedOrPaymentPending: {
      if (isManagerLevelEditAllowed(map)) {
        return true;
      }

      if (hasManagerLevelEditActions(map)) {
        return false;
      }

      return undefined;
    }

    case AccountsPayableAction.ReferToCompanyAdmin: {
      if (
        isApActionAllowed(map, AccountsPayableBackendAction.Refer) ||
        isApActionAllowed(map, AccountsPayableBackendAction.ReferToCompanyAdmin)
      ) {
        return true;
      }

      if (
        hasApAction(map, AccountsPayableBackendAction.Refer) ||
        hasApAction(map, AccountsPayableBackendAction.ReferToCompanyAdmin)
      ) {
        return false;
      }

      return undefined;
    }

    case AccountsPayableAction.CollatedPayment: {
      if (isApActionAllowed(map, AccountsPayableBackendAction.CollatedPayment)) {
        return true;
      }

      if (hasApAction(map, AccountsPayableBackendAction.CollatedPayment)) {
        return false;
      }

      return undefined;
    }

    case AccountsPayableAction.Duplicate: {
      if (
        isApActionAllowed(map, AccountsPayableBackendAction.Duplicate) ||
        isApActionAllowed(map, 'MakeACopy')
      ) {
        return true;
      }

      if (
        hasApAction(map, AccountsPayableBackendAction.Duplicate) ||
        hasApAction(map, 'MakeACopy')
      ) {
        return false;
      }

      return undefined;
    }

    case AccountsPayableAction.IntegrateToNetSuite: {
      if (
        isApActionAllowed(map, AccountsPayableBackendAction.IntegrateToNetSuite) ||
        isApActionAllowed(map, AccountsPayableBackendAction.IntegrateNetSuite)
      ) {
        return true;
      }

      if (
        hasApAction(map, AccountsPayableBackendAction.IntegrateToNetSuite) ||
        hasApAction(map, AccountsPayableBackendAction.IntegrateNetSuite)
      ) {
        return false;
      }

      return undefined;
    }

    default: {
      if (isApActionAllowed(map, feAction)) {
        return true;
      }

      if (hasApAction(map, feAction)) {
        return false;
      }

      return undefined;
    }
  }
}

export function hasAccountsPayableModuleActionPermissions(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined
): boolean {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );
  return Boolean(
    companyPermission?.actionPermissions?.some(
      (permission) => permission.module === ACCOUNTS_PAYABLE_PERMISSION_MODULE
    )
  );
}

export function resolveAccountsPayableActionAllowed(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  _selectedRoleId: string | null | undefined,
  action: AccountsPayableActionName | string,
  legacyAllowed: boolean
): boolean {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  // Any role after /my: when pagePermissions or AP actionPermissions exist, use BE only.
  const usesBePermissions =
    usesApiPagePermissions(companyPermission) ||
    hasAccountsPayableModuleActionPermissions(
      storedCompanyPermissions,
      companyId
    );

  if (!usesBePermissions) {
    return legacyAllowed;
  }

  const actionPermissions = companyPermission?.actionPermissions ?? [];
  const resolved = resolveFeActionFromBeActionMap(
    buildApActionMap(actionPermissions),
    action,
    companyPermission
  );

  if (resolved !== undefined) {
    return resolved;
  }

  return false;
}

export function getAllowedAccountsPayableActions(
  storedCompanyPermissions: CompanyPermission[] | null | undefined,
  companyId: string | null | undefined,
  _selectedRoleId?: string | null | undefined
): Set<string> {
  const companyPermission = getCompanyPermissionForCompany(
    storedCompanyPermissions,
    companyId
  );

  const usesBePermissions =
    usesApiPagePermissions(companyPermission) ||
    hasAccountsPayableModuleActionPermissions(
      storedCompanyPermissions,
      companyId
    );

  if (!usesBePermissions) {
    return new Set();
  }

  const actionPermissions = companyPermission?.actionPermissions ?? [];
  const map = buildApActionMap(actionPermissions);
  const allowed = new Set<string>();

  (Object.values(AccountsPayableAction) as string[]).forEach((feAction) => {
    if (resolveFeActionFromBeActionMap(map, feAction, companyPermission) === true) {
      allowed.add(feAction);
    }
  });

  return allowed;
}

type LegacyPermissionDeps = {
  roleIds: string[];
  can: (module: 'ap', action: 'view' | 'create' | 'edit' | 'delete') => boolean;
  bypassApManager1?: boolean;
};

/** Role-map fallback when action is missing from BE response (or non-API role). */
export function getLegacyAccountsPayableActionAllowed(
  action: AccountsPayableActionName | string,
  deps: LegacyPermissionDeps
): boolean {
  const { roleIds, can, bypassApManager1 = false } = deps;
  const isCreator = roleIds.includes(RoleConstant.accountsPayableCreator);
  const isAp1 = roleIds.includes(RoleConstant.accountsPayableManager1);
  const isAp2 = roleIds.includes(RoleConstant.accountsPayableManager2);
  const isAdmin = roleIds.includes(RoleConstant.companyAdminId);
  const isApManagerApprover = isAp2 || (isAp1 && !bypassApManager1);

  switch (action) {
    case AccountsPayableAction.Create:
      return isCreator || can('ap', 'create');
    case AccountsPayableAction.View:
      return can('ap', 'view');
    case AccountsPayableAction.DeleteDraft:
    case AccountsPayableAction.Withdraw:
      return can('ap', 'delete');
    case AccountsPayableAction.Duplicate:
      return isCreator;
    case AccountsPayableAction.EditDraftOrReturned:
    case AccountsPayableAction.EditApprovedOrPaymentPending:
      return can('ap', 'edit');
    case AccountsPayableAction.Approve:
    case AccountsPayableAction.Reject:
    case AccountsPayableAction.Return:
      return isApManagerApprover || isAdmin;
    case AccountsPayableAction.MakePayment:
    case AccountsPayableAction.EditPayment:
      return isApManagerApprover || isAdmin;
    case AccountsPayableAction.ReferToCompanyAdmin:
      return isAp2;
    case AccountsPayableAction.CollatedPayment:
      return isApManagerApprover || isAdmin;
    case AccountsPayableAction.IntegrateToNetSuite:
      return isApManagerApprover || isAdmin;
    case AccountsPayableAction.Restore:
      return can('ap', 'edit') || isAdmin || isCreator;
    default:
      return false;
  }
}
